import apiClient from './api_client';

/* ------------------------------------------------------------
 * Acquisition attribution (Google Ads and other campaigns).
 *
 * Nothing is written to the device: the params are read from the URL at
 * bootstrap, kept in memory, and sent server-side once a tenant is known.
 * The trade-off is the window: attribution lives for one page session, not
 * thirty days, so a visitor who signs up on a later visit is not attributed.
 * ------------------------------------------------------------ */

const MAX_VALUE_LENGTH = 255;
const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid'
] as const;

/** Write-only from the frontend: sent once and never read back. */
type UserAttributionParams = Partial<
    Record<(typeof ATTRIBUTION_KEYS)[number], string>
>;

let pending: UserAttributionParams | null = null;
let flushing = false;
let googleAds = false;

/**
 * Read tracking params from the current URL. Must run before the router mounts,
 * since it rewrites the URL. First-touch: an already captured attribution is
 * never replaced.
 */
export function captureAttributionFromUrl(
    search: string = window.location.search
): void {
    if (pending) return;

    const params = new URLSearchParams(search);
    const captured: UserAttributionParams = {};

    for (const key of ATTRIBUTION_KEYS) {
        const value = params.get(key)?.trim();
        if (value) captured[key] = value.slice(0, MAX_VALUE_LENGTH);
    }

    if (Object.keys(captured).length) {
        pending = captured;
        googleAds = captured.utm_source?.toLowerCase() === 'google';
    }
}

/**
 * Campaign traffic goes straight to the prompt step. Derived at capture, so it
 * still answers once `pending` has been flushed.
 */
export function shouldDisplayOnboardingStepOne(): boolean {
    return !googleAds;
}

/**
 * Callback URL for a sign-up flow that leaves the page, as Google and Microsoft
 * do: the query string is the only place the params survive a full page load.
 * Undefined when there is nothing to carry, so the flow keeps the redirect
 * configured in Descope.
 */
export function attributionRedirectUrl(): string | undefined {
    if (!pending) return undefined;

    const url = new URL(window.location.pathname, window.location.origin);
    for (const [key, value] of Object.entries(pending)) {
        url.searchParams.set(key, value);
    }
    return url.toString();
}

/**
 * First-touch is enforced by the backend too: if the user already has an
 * attribution it keeps that one and ignores what is sent here, so a repeat
 * call is a no-op rather than an error.
 */
async function recordUserAttribution(
    tenantId: string,
    params: UserAttributionParams
): Promise<void> {
    await apiClient.post('/user_attribution', {
        tenant_id: tenantId,
        ...params
    });
}

/**
 * Send a pending attribution to the backend, then forget it. Kept in memory
 * when the call fails, so a tenant change retries it within this page session.
 * Fire-and-forget: callers must not await it on a navigation path.
 */
export async function flushAttribution(tenantId?: string): Promise<void> {
    if (flushing || !tenantId || !pending) return;

    flushing = true;
    try {
        await recordUserAttribution(tenantId, pending);
        pending = null;
    } catch (error) {
        console.error('Failed to send acquisition attribution', error);
    } finally {
        flushing = false;
    }
}
